import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useI18n } from '~/i18n/i18n';

interface DayPoint {
  name: string;
  value: number;
}

interface DailyPlaysChartProps {
  /** Legacy single-series data. Used when widgetData/checkoutData are not provided. */
  weeklyData?: DayPoint[];
  /** Two-series mode: per-channel daily plays. When both are passed, two areas render. */
  widgetData?: DayPoint[];
  checkoutData?: DayPoint[];
  isLoading?: boolean;
}

const WIDGET_COLOR = '#5b5cf6';
const CHECKOUT_COLOR = '#ec4899';

/** Merge two per-day series (keyed by name) into one dataset with widget/checkout keys. */
function mergeSeries(widget: DayPoint[], checkout: DayPoint[]) {
  const byName = new Map<string, { name: string; widget: number; checkout: number }>();
  const order: string[] = [];
  const ingest = (rows: DayPoint[], key: 'widget' | 'checkout') => {
    rows.forEach((d) => {
      let row = byName.get(d.name);
      if (!row) {
        row = { name: d.name, widget: 0, checkout: 0 };
        byName.set(d.name, row);
        order.push(d.name);
      }
      row[key] = d.value || 0;
    });
  };
  ingest(widget || [], 'widget');
  ingest(checkout || [], 'checkout');
  return order.map((n) => byName.get(n)!);
}

const DailyPlaysChart: React.FC<DailyPlaysChartProps> = ({
  weeklyData,
  widgetData,
  checkoutData,
  isLoading = false,
}) => {
  const { t, locale } = useI18n();
  const isRTL = locale === 'he';

  // Activate two-series mode only when the per-channel split actually has data.
  // Otherwise (e.g. legacy plays not yet attributed to a channel) fall back to
  // the combined single-series chart from weeklyData.
  const splitTotal =
    (widgetData || []).reduce((s, d) => s + (d.value || 0), 0) +
    (checkoutData || []).reduce((s, d) => s + (d.value || 0), 0);
  const twoSeries = Array.isArray(widgetData) && Array.isArray(checkoutData) && splitTotal > 0;

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-[0px_3px_8px_0px_rgba(44,44,44,0.06)] p-4 lg:p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  // --- Two-series (Widget vs Checkout) mode -------------------------------
  if (twoSeries) {
    const merged = mergeSeries(widgetData!, checkoutData!);
    const widgetTotal = (widgetData || []).reduce((s, d) => s + (d.value || 0), 0);
    const checkoutTotal = (checkoutData || []).reduce((s, d) => s + (d.value || 0), 0);
    const grandTotal = widgetTotal + checkoutTotal;

    // Render nothing if BOTH channels have no plays.
    if (merged.length === 0 || grandTotal === 0) {
      return null;
    }

    const labels: Record<string, string> = {
      widget: t('dashboard.charts.widgetPlays'),
      checkout: t('dashboard.charts.checkoutPlays'),
    };

    const CustomTooltip = ({ active, payload, label }: any) => {
      if (active && payload && payload.length) {
        return (
          <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
            <p className="text-gray-600 text-sm font-medium mb-1">{`${t('common.date')}: ${label}`}</p>
            {payload.map((p: any) => (
              <p key={p.dataKey} className="text-sm font-bold" style={{ color: p.color }}>
                {`${labels[p.dataKey] || p.dataKey}: ${p.value}`}
              </p>
            ))}
          </div>
        );
      }
      return null;
    };

    return (
      <div className="bg-white rounded-xl shadow-[0px_3px_8px_0px_rgba(44,44,44,0.06)] p-4 lg:p-6">
        <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div>
            <h3 className="font-semibold text-[#2c2c2c] text-[16px] lg:text-[18px]">
              {t('dashboard.charts.dailyPlays')}
            </h3>
            <p className="text-[#6a6a6a] text-[12px] lg:text-[13px] mt-1">
              {t('dashboard.charts.last7Days')}
            </p>
          </div>
        </div>

        <div className="h-64 lg:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={merged} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorWidget" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={WIDGET_COLOR} stopOpacity={0.7} />
                  <stop offset="95%" stopColor={WIDGET_COLOR} stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="colorCheckout" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHECKOUT_COLOR} stopOpacity={0.7} />
                  <stop offset="95%" stopColor={CHECKOUT_COLOR} stopOpacity={0.05} />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="name" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis
                stroke="#9ca3af"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                formatter={(value: string) => labels[value] || value}
                wrapperStyle={{ fontSize: '13px', paddingTop: '8px' }}
              />

              <Area
                type="monotone"
                dataKey="widget"
                stroke={WIDGET_COLOR}
                strokeWidth={2}
                fill="url(#colorWidget)"
                fillOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey="checkout"
                stroke={CHECKOUT_COLOR}
                strokeWidth={2}
                fill="url(#colorCheckout)"
                fillOpacity={0.6}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Summary stats below chart */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className={`flex items-center justify-between text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
            <span className="text-[#6a6a6a]">
              {t('dashboard.stats.total')} {t('dashboard.kpis.plays')} {t('dashboard.timeframes.thisWeek')}:
            </span>
            <span className="font-semibold text-[#2c2c2c]">{grandTotal.toLocaleString()}</span>
          </div>
          <div className={`flex items-center justify-between text-sm mt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <span className="text-[#6a6a6a]" style={{ color: WIDGET_COLOR }}>{labels.widget}:</span>
            <span className="font-semibold" style={{ color: WIDGET_COLOR }}>{widgetTotal.toLocaleString()}</span>
          </div>
          <div className={`flex items-center justify-between text-sm mt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <span className="text-[#6a6a6a]" style={{ color: CHECKOUT_COLOR }}>{labels.checkout}:</span>
            <span className="font-semibold" style={{ color: CHECKOUT_COLOR }}>{checkoutTotal.toLocaleString()}</span>
          </div>
        </div>
      </div>
    );
  }

  // --- Legacy single-series mode (unchanged behaviour) --------------------
  const series = weeklyData || [];
  const hasRealData = series.some((day) => day.value > 0);
  const totalPlays = series.reduce((sum, day) => sum + day.value, 0);

  if (!series || series.length === 0 || !hasRealData || totalPlays === 0) {
    return null;
  }

  const averagePlays = Math.round(totalPlays / series.length);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="text-gray-600 text-sm font-medium">{`${t('common.date')}: ${label}`}</p>
          <p className="text-[#696cff] text-lg font-bold">
            {`${payload[0].value} ${t('dashboard.kpis.plays')}`}
          </p>
          {payload[0].value === 0 && (
            <p className="text-gray-400 text-xs mt-1">{t('dashboard.sections.noGameData')}</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-xl shadow-[0px_3px_8px_0px_rgba(44,44,44,0.06)] p-4 lg:p-6">
      <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div>
          <h3 className="font-semibold text-[#2c2c2c] text-[16px] lg:text-[18px]">
            {t('dashboard.charts.dailyPlays')}
          </h3>
          <p className="text-[#6a6a6a] text-[12px] lg:text-[13px] mt-1">
            {t('dashboard.charts.last7Days')}
          </p>
        </div>
      </div>

      <div className="h-64 lg:h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={series}
            margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0,
            }}
          >
            <defs>
              <linearGradient id="colorPlays" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#696cff" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#696cff" stopOpacity={0.1}/>
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#f1f5f9"
              vertical={false}
            />

            <XAxis
              dataKey="name"
              stroke="#9ca3af"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />

            <YAxis
              stroke="#9ca3af"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => value.toLocaleString()}
            />

            <Tooltip content={<CustomTooltip />} />

            <Area
              type="monotone"
              dataKey="value"
              stroke="#696cff"
              strokeWidth={2}
              fill="url(#colorPlays)"
              fillOpacity={0.6}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Summary stats below chart */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className={`flex items-center justify-between text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
          <span className="text-[#6a6a6a]">{t('dashboard.stats.total')} {t('dashboard.kpis.plays')} {t('dashboard.timeframes.thisWeek')}:</span>
          <span className="font-semibold text-[#2c2c2c]">
            {totalPlays.toLocaleString()}
          </span>
        </div>
        <div className={`flex items-center justify-between text-sm mt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <span className="text-[#6a6a6a]">{t('dashboard.stats.average')} {t('common.per')} {t('common.day')}:</span>
          <span className="font-semibold text-[#2c2c2c]">
            {averagePlays.toLocaleString()}
          </span>
        </div>
        <div className={`flex items-center justify-between text-sm mt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <span className="text-[#6a6a6a]">{t('dashboard.sections.mostActive')} {t('common.day')}:</span>
          <span className="font-semibold text-[#2c2c2c]">
            {series.reduce((max, day) => day.value > max.value ? day : max, series[0]).name}
          </span>
        </div>
      </div>
    </div>
  );
};

export default DailyPlaysChart;
